import type { CSSProperties } from "react"
import { Anton, Fraunces, IBM_Plex_Mono, Inter, Plus_Jakarta_Sans } from "next/font/google"
import localFont from "next/font/local"
import "./fonts/dancing-script-fallback.css"

import { COLORS } from "@/lib/design-tokens"

// Source Serif 4 (the old --font-fraunces binding) is not loaded: no selector
// read var(--font-fraunces), so its 36 faces only added bytes to every page.
// The Fraunces below is bound to --font-display, and every selector that wants
// the display serif reads var(--font-display).

// High-contrast editorial serif used as the marketing display face. Closest
// free match to Oura's "Editorial New" (light weight, tight tracking) adopted
// for the Oura-styled marketing surfaces (owner directive 2026-06-26).
const fraunces = Fraunces({
  variable: "--font-display",
  subsets: ["latin"],
  weight: ["300", "400", "500"],
  display: "swap",
})

const inter = Inter({
  variable: "--font-news",
  subsets: ["latin"],
  display: "swap",
  // No public text renders in Inter first: it only trails var(--font-jakarta)
  // as a fallback, so preloading it spends first-paint bandwidth on a face no
  // visitor reads. The binding stays global and the @font-face stays
  // registered; the browser fetches it only if something does use it.
  preload: false,
})

const ibmPlexMono = IBM_Plex_Mono({
  variable: "--font-mono",
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
  display: "swap",
  // Not preloaded here, because this binding reaches every route and the
  // landing, /apply and /mentorship first screens render no text in it.
  // Route-specific preloads live in web/lib/mono-font-preload.ts for the
  // article listing, subscription page and legal PolicyPage. Article detail
  // uses web/lib/article-mono-font-preload.ts for its regular-weight rail.
  // The @font-face stays registered and loads the moment a label needs it.
  preload: false,
})

// A script face for ONE line, the "You just show up." in the Done-For-You
// closing band (owner, this session, with the source's own treatment attached).
// It is Dancing Script because that is literally what the source sets there:
// its CSS bundle declares `font-family:Dancing Script,cursive` on that element.
//
// This is a deliberate exception to the type system, which is otherwise Inter
// everywhere with Fraunces surviving only in the masthead wordmark and the
// footer newsletter heading. A fourth family earns its place on exactly one
// line of one page. If it starts showing up anywhere else, that is drift, not a
// decision: the whole point of the line is that nothing else on the site looks
// like it.
// The DECIDED site family (owner). Bound globally so var(--font-jakarta)
// resolves on every page - the landing sets it as its base, /compare's
// modules use it, and the coming site-wide font pass flips pages by
// referencing the var rather than adding bindings one page at a time.
const jakarta = Plus_Jakarta_Sans({
  variable: "--font-jakarta",
  subsets: ["latin"],
})

// The caps accent face, bound globally for the same reason Jakarta is. It is
// referenced by shared components that render on several pages - the coach
// strips' ghost numerals are the live case - so a landing-only binding left
// var(--font-anton) undefined everywhere else, which makes the whole
// declaration invalid and silently drops the numerals into the inherited body
// face. A shared component cannot depend on a binding one page happens to set.
const anton = Anton({
  variable: "--font-anton",
  subsets: ["latin"],
  weight: "400",
  // Ghost numerals and caps accents sit at or below the fold on the pages that
  // use it (on a phone the landing's second heading starts at the fold, and
  // next/font's size-matched fallback paints it until the file arrives), and
  // articles and /apply never use it. The binding stays
  // global (see the note above); only the preload is off, so the file loads
  // on demand where a numeral does render.
  preload: false,
})

const dancingScript = localFont({
  src: "./fonts/dancing-script-latin.woff2",
  variable: "--font-script",
  weight: "400 500",
  style: "normal",
  display: "swap",
  // Keep the established Arial fallback metrics from the scoped face below.
  adjustFontFallback: false,
  fallback: ["'Dancing Script Fallback'"],
  // Used on exactly one line of one page (see the note above), so it is not
  // preloaded on every page of the site. display:swap already governs how that
  // one line paints while the file arrives.
  preload: false,
})

// Lato and Roboto (the Slack and Google Calendar replica faces) are bound in
// web/modules/community-see-inside/instances.tsx, the only module that reads
// var(--font-lato) and var(--font-roboto), so no other page carries them.

export const fontVariableClassName = `${fraunces.variable} ${inter.variable} ${ibmPlexMono.variable} ${dancingScript.variable} ${jakarta.variable} ${anton.variable}`

type ThemeVars = CSSProperties & Record<`--${string}`, string>

export const lightTheme: ThemeVars = {
  "--color-paper": COLORS.paper,
  "--color-paper-high": COLORS.paperHigh,
  "--color-paper-deep": COLORS.paperDeep,
  "--color-ink": COLORS.ink,
  "--color-ink-muted": COLORS.inkMuted,
  "--color-ink-soft": COLORS.inkSoft,
  "--color-rule": COLORS.rule,
  "--color-rule-strong": COLORS.ruleStrong,
  "--color-vermilion": COLORS.vermilion,
  "--color-vermilion-soft": "var(--color-vermilion)",
  "--color-oxblood": COLORS.oxblood,
  "--color-device-shell": COLORS.deviceShell,
  "--color-device-detail": COLORS.deviceDetail,
  "--color-night": COLORS.night,
  "--color-night-ink": COLORS.nightInk,
  "--color-bg": "var(--color-paper)",
  "--color-surface": "var(--color-paper-high)",
  "--color-accent": "var(--color-vermilion)",
  "--color-accent-contrast": "var(--color-ink)",
  "--color-focus": "var(--color-vermilion)",
}
